import { useEffect, useRef, useState } from 'react';
import { logger } from '@/app/logger';
import { fetchPublishedArticles, peekPublishedArticles } from '../lib/articlesPublicApi';
import { CONTENT_LIST_PAGE_COPY } from '../lib/contentListPageConstants';
import type { ListPatternProps } from '../lib/contentListPageTypes';
import {
  INITIAL_LIST_PATTERN_STATE,
  listPatternStateError,
  listPatternStateFromLoaded,
  listPatternStateLoading,
  type ListPatternState,
} from '../lib/listPatternState';

const log = logger.create('useListPatternItems');

function resolveUserErrorMessage(
  err: unknown,
  overrideMessage?: string,
): string {
  if (overrideMessage) return overrideMessage;
  if (err instanceof Error && err.message) return err.message;
  return CONTENT_LIST_PAGE_COPY.defaultError;
}

export function useListPatternItems(
  listProps: ListPatternProps | undefined,
  pageTitle: string,
): ListPatternState {
  const [state, setState] = useState<ListPatternState>(() => {
    if (!listProps?.fetchUrl) {
      return INITIAL_LIST_PATTERN_STATE;
    }
    const cached = peekPublishedArticles(listProps.fetchUrl);
    if (cached) {
      return listPatternStateFromLoaded(cached);
    }
    return listPatternStateLoading();
  });
  const filterFnRef = useRef(listProps?.filterFn);
  const errorMessageRef = useRef(listProps?.errorMessage);

  filterFnRef.current = listProps?.filterFn;
  errorMessageRef.current = listProps?.errorMessage;

  const fetchUrl = listProps?.fetchUrl;

  useEffect(() => {
    if (!fetchUrl) {
      setState(INITIAL_LIST_PATTERN_STATE);
      return;
    }

    let cancelled = false;

    const cached = peekPublishedArticles(fetchUrl);
    if (!cached) {
      setState(listPatternStateLoading());
    }
    log.info('fetch', 'Starting article list fetch', { fetchUrl, pageTitle });

    fetchPublishedArticles(fetchUrl)
      .then((data) => (filterFnRef.current ? filterFnRef.current(data) : data))
      .then((items) => {
        if (cancelled) return;
        log.info('fetch', 'Article list fetch succeeded', {
          fetchUrl,
          pageTitle,
          count: items.length,
        });
        setState(listPatternStateFromLoaded(items));
      })
      .catch((err) => {
        if (cancelled) return;
        const message = resolveUserErrorMessage(err, errorMessageRef.current);
        log.error('fetch', 'Article list fetch failed', { fetchUrl, pageTitle, message });
        setState(listPatternStateError(message));
      });

    return () => {
      cancelled = true;
    };
  }, [fetchUrl, pageTitle]);

  return state;
}
